import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_WIDTH = 176;
const MENU_ESTIMATE_HEIGHT = 180;

function ActionDotsIcon() {
  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  );
}

function isTriggerVisible(trigger) {
  if (!trigger) return false;
  const rect = trigger.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (rect.bottom < 0 || rect.top > window.innerHeight) return false;
  if (rect.right < 0 || rect.left > window.innerWidth) return false;
  return true;
}

export default function TableActionMenu({ isOpen, onToggle, onClose, children, ariaLabel = 'Actions' }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || !isTriggerVisible(trigger)) {
      setPos(null);
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    let left = rect.right - MENU_WIDTH;
    left = Math.max(margin, Math.min(left, window.innerWidth - MENU_WIDTH - margin));

    let top = rect.bottom + 6;
    if (top + MENU_ESTIMATE_HEIGHT > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - MENU_ESTIMATE_HEIGHT - 6);
    }

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null);
      return;
    }
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onReflow = () => updatePosition();
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const showMenu = isOpen && pos != null;

  return (
    <div className="relative flex justify-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={onToggle}
        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
        aria-label={ariaLabel}
        aria-expanded={showMenu}
        aria-haspopup="menu"
      >
        <ActionDotsIcon />
      </button>

      {showMenu && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed w-44 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-[200] py-1"
          style={{ top: pos.top, left: pos.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body,
      )}
    </div>
  );
}
